import { createAction, props } from '@ngrx/store';
import { ErrorModel } from '../common.models';
import {
    Draft,
    SaveDraft,
    ValidateListingResponse,
    ValidationResult,
    ListingResponse,
    AddPhoto,
    PhotoUrls,
    UpdateCurrentStep,
    CategoryEnum,
    ListingRequest,
    ListingRequestType,
    SaveDraftResponse
} from '@classifieds-sell/datalayer';
import {
    BoundingBox,
    EditedPhotoData,
    Photo,
    SelectedPhotoData,
    TransformedPhoto
} from '@classifieds-sell/trademe-core-ui';
import { PropertyAgentContactDetailsType } from 'libs/property-ui/src/lib/models/property-agent-contact-details/property-agent-contact-details-model';

export const noOpAction = createAction('[Draft] No op Action');

export const getDraft = createAction(
    '[Draft/API] Get Draft',
    props<{
        categoryId: CategoryEnum;
        subCategory: string;
        listingId?: number;
    }>()
);

export const getDraftSuccess = createAction(
    '[Draft/API] Get Draft Success',
    props<{ draft: Draft }>()
);

export const getDraftFailure = createAction(
    '[Draft/API] Get Draft Failure',
    props<{ error: ErrorModel }>()
);

export const updateDraftData = createAction(
    '[Draft/API] Update Draft Data',
    props<{ draftData: any }>()
);

export const updateValidationResult = createAction(
    '[Draft/API] Update Validation Results',
    props<{ validationResult: ValidationResult }>()
);

export const saveDraft = createAction(
    '[Draft/API] Save Draft',
    props<{ draft: SaveDraft; }>()
);

export const saveDraftSuccess = createAction(
    '[Draft/API] Save Draft Success',
    props<{ result: SaveDraftResponse; lastSavedStepId: string }>()
);

export const saveDraftFailure = createAction(
    '[Draft/API] Save Draft Failure',
    props<{ error: ErrorModel }>()
);

export const resetSaveDraftLoadingStatus = createAction(
    '[Draft/API] Reset Save Draft Loading Status'
);

export const makeListingAction = createAction(
    '[Draft/API] Make Listing Action',
    props<{ listingRequest: ListingRequest }>()
);

export const makeListingActionSuccess = createAction(
    '[Draft/API] Make Listing Action Success',
    props<{
        listingRequest: ListingResponse;
        listingRequestType: ListingRequestType;
    }>()
);

export const makeListingActionFailure = createAction(
    '[Draft/API] Make Listing Action Failure',
    props<{
        error: ErrorModel;
        listingRequestType: ListingRequestType;
    }>()
);

export const createListingFailure = createAction(
    '[Draft/API] Create Listing Failure',
    props<{ error: ErrorModel }>()
);

export const validateListing = createAction('[Draft/API] Validate Listing');

export const validateListingSuccess = createAction(
    '[Draft/API] Validate Listing Success',
    props<{ validListing: ValidateListingResponse }>()
);

export const validateListingFailure = createAction(
    '[Draft/API] Validate Listing Failure',
    props<{ error: ErrorModel }>()
);

export const addPhotoToDraftData = createAction(
    '[Draft] Add Photo To Draft Data',
    props<{
        photo: Photo;
        selectedPhotoData: SelectedPhotoData;
        parentProperty: string;
    }>()
);

export const addAgentPhotoToDraftData = createAction(
    '[Draft] Add Agent Photo To Draft Data',
    props<{
        photo: Photo;
        selectedPhotoData: SelectedPhotoData;
        agenttContactDetailsType: PropertyAgentContactDetailsType;
        parentProperty: string;
    }>()
);

export const uploadPhoto = createAction(
    '[Draft/API] Upload Photo',
    props<{
        photoId: string;
        selectedPhotoData: AddPhoto;
        parentProperty: string;
    }>()
);

export const uploadAgentPhoto = createAction(
    '[Draft/API] Upload Agent Photo',
    props<{
        photoId: string;
        selectedPhotoData: SelectedPhotoData;
        agenttContactDetailsType: PropertyAgentContactDetailsType;
        parentProperty: string;
    }>()
);

export const uploadPhotoSuccess = createAction(
    '[Draft/API] Upload Photo Success',
    props<{ photoId: string; photoUrls: PhotoUrls; parentProperty: string }>()
);

export const uploadPhotoFailure = createAction(
    '[Draft/API] Upload Photo Failure',
    props<{ photoId: string; error: ErrorModel; parentProperty: string }>()
);

export const uploadAgentPhotoSuccess = createAction(
    '[Draft/API] Upload Agent Photo Success',
    props<{ photoId: string; photoUrl: string; agenttContactDetailsType: PropertyAgentContactDetailsType; parentProperty: string }>()
);

export const uploadAgentPhotoFailure = createAction(
    '[Draft/API] Upload Agent Photo Failure',
    props<{ photoId: string; error: ErrorModel; agenttContactDetailsType: PropertyAgentContactDetailsType; parentProperty: string }>()
);

export const removeAgentPhotoFromDraftData = createAction(
    '[Draft] Remove Agent Photo From Draft Data',
    props<{ agenttContactDetailsType: PropertyAgentContactDetailsType; parentProperty: string }>()
);

export const updatePhotosInDraftData = createAction(
    '[Draft] Update Photos In Draft Data',
    props<{ photos: Photo[]; parentProperty: string }>()
);

export const updateTransformPhotoDataToDraftData = createAction(
    '[Draft] Update Transform Photo Data To Draft Data',
    props<{ editedPhotoData: EditedPhotoData; parentProperty: string }>()
);

export const transformPhoto = createAction(
    '[Draft/API] Transform Photo',
    props<{
        photoId: string;
        editedPhotoData: EditedPhotoData;
        parentProperty: string;
    }>()
);

export const transformPhotoSuccess = createAction(
    '[Draft/API] Transform Photo Success',
    props<{
        photoId: string;
        transformedPhoto: TransformedPhoto;
        crop: BoundingBox;
        parentProperty: string;
    }>()
);

export const transformPhotoFailure = createAction(
    '[Draft/API] Transform Photo Failure',
    props<{ photoId: string; error: ErrorModel; parentProperty: string }>()
);

export const updateInvalidPhoto = createAction(
    '[Draft] Update Invalid Photo',
    props<{ photoId: string; errorMessage: string; parentProperty: string }>()
);

export const updateCurrentStep = createAction(
    '[Draft/API] Update Current Step',
    props<{ currentStep: UpdateCurrentStep }>()
);

export const updateCurrentStepSuccess = createAction(
    '[Draft/API] Update Current Step Success',
    props<{ result: boolean; currentStepId: string }>()
);

export const updateCurrentStepFailure = createAction(
    '[Draft/API] Update Current Step Failure',
    props<{ error: ErrorModel }>()
);

export const resetPackageCodeIfNeeded = createAction(
    '[Draft] Reset Package Code If Needed',
    props<{ isRelist: boolean, isAgent: boolean }>()
)
